import { NapiDataSource } from './napi'
import type { VehicleDataSource } from './types'
import type { UserObjType, FullVehicleStatus2 } from '@/types/bluelink'
import { apiClient, ApiError } from '@/services/apiClient'

export interface BackgroundTaskState {
  failed_at: string
  error: string
}

export interface HealthStatus {
  db: boolean
  polling: boolean
  last_poll: string | null
  rate_limited: boolean
  rate_limited_until: string | null
  dominant_failure_reason: string | null
  starting_up: boolean
  background_tasks?: Record<string, BackgroundTaskState>
}

export type HealthFetchResult =
  | { kind: 'ok'; status: HealthStatus }
  | { kind: 'unreachable' }
  | { kind: 'unknown' }

export async function fetchHealth(): Promise<HealthFetchResult> {
  try {
    const resp = await fetch(apiUrl('/api/v1/polling-status'))
    if (resp.ok) return { kind: 'ok', status: await resp.json() as HealthStatus }
    if (resp.status !== 502 && resp.status !== 503 && resp.status !== 504) return { kind: 'unknown' }
  } catch {
    // network error — fall through to single retry
  }
  await new Promise<void>(resolve => setTimeout(resolve, 500))
  try {
    const resp = await fetch(apiUrl('/api/v1/polling-status'))
    if (resp.ok) return { kind: 'ok', status: await resp.json() as HealthStatus }
  } catch {
    // second attempt failed
  }
  return { kind: 'unreachable' }
}

const COMMAND_ENDPOINTS: Record<string, string> = {
  BluelinkLock: '/api/v1/commands/lock',
  BluelinkUnlock: '/api/v1/commands/unlock',
  BluelinkClimateStart: '/api/v1/commands/climate/start',
  BluelinkClimateStop: '/api/v1/commands/climate/stop',
  BluelinkHornLights: '/api/v1/commands/horn',
  BluelinkChargeStart: '/api/v1/commands/charge/start',
}

function apiUrl(path: string): string {
  const base = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
  return `${base}${path}`
}

async function getToken(): Promise<string | null> {
  const { useAuthStore } = await import('@/stores/auth')
  return useAuthStore().accessToken
}

async function handleUnauthorized(): Promise<void> {
  const [{ useAuthStore }, { default: router }] = await Promise.all([
    import('@/stores/auth'),
    import('@/router'),
  ])
  useAuthStore().clearSession()
  void router.push('/login')
}

export class BackendDataSource implements VehicleDataSource {
  private napi = new NapiDataSource()

  connect(): void { this.napi.connect() }
  disconnect(): void { this.napi.disconnect() }
  onReconnect(_handler: () => void): () => void { return () => {} }
  onceSessionReady(fn: () => void): void { fn() }
  get connected(): boolean { return this.napi.connected }

  loginWithPassword(mobile: string, password: string): Promise<{ accessToken: string; user: UserObjType }> {
    return this.napi.loginWithPassword(mobile, password)
  }

  reauthWithToken(token: string): Promise<UserObjType | null> {
    return this.napi.reauthWithToken(token)
  }

  subscribeStatus(
    onAck: () => void,
    onData: (s: FullVehicleStatus2) => void,
    onError: () => void,
    _onMissingVin?: () => void,
    onReconnecting?: () => void,
  ): () => void {
    let aborted = false
    let lastEventId: string | null = null
    let controller = new AbortController()
    let wakeFromSleep: (() => void) | null = null

    const PRE_ACK_BACKOFF_MS = [500, 1000, 2000]
    const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30_000]
    const MAX_POST_ACK_FAILURES = 6

    function interruptibleSleep(ms: number): Promise<void> {
      return new Promise<void>(resolve => {
        const timer = setTimeout(resolve, ms)
        wakeFromSleep = () => { clearTimeout(timer); resolve() }
      })
    }

    void (async () => {
      let attempt = 0
      let acked = false
      let initialAttempt = 0
      let postAckFailures = 0
      const MAX_INITIAL_ATTEMPTS = PRE_ACK_BACKOFF_MS.length

      while (!aborted) {
        const signal = controller.signal

        const token = await getToken()
        if (aborted) return
        if (!token) { onError(); return }

        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        }
        if (lastEventId !== null) headers['Last-Event-ID'] = lastEventId

        try {
          const resp = await fetch(apiUrl('/api/v1/stream'), { headers, signal })

          if (resp.status === 401) { onError(); await handleUnauthorized(); return }

          if (!resp.ok) {
            if (!acked) {
              if (initialAttempt >= MAX_INITIAL_ATTEMPTS) { onError(); return }
              initialAttempt++
              onReconnecting?.()
              controller = new AbortController()
              await interruptibleSleep(PRE_ACK_BACKOFF_MS[Math.min(initialAttempt - 1, PRE_ACK_BACKOFF_MS.length - 1)])
              if (aborted) return
              continue
            }
            postAckFailures++
            if (postAckFailures > MAX_POST_ACK_FAILURES) { onError(); return }
            onReconnecting?.()
            controller = new AbortController()
            await interruptibleSleep(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)])
            attempt++
            continue
          }

          const contentType = resp.headers.get('content-type') ?? ''
          if (!contentType.toLowerCase().includes('text/event-stream')) {
            if (!acked) {
              if (initialAttempt >= MAX_INITIAL_ATTEMPTS) { onError(); return }
              initialAttempt++
              onReconnecting?.()
              controller = new AbortController()
              await interruptibleSleep(PRE_ACK_BACKOFF_MS[Math.min(initialAttempt - 1, PRE_ACK_BACKOFF_MS.length - 1)])
              if (aborted) return
              continue
            }
            onError()
            return
          }

          if (!acked) { acked = true; onAck() }
          attempt = 0
          postAckFailures = 0

          const reader = resp.body!.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''
            for (const line of lines) {
              if (line.startsWith('id: ')) {
                lastEventId = line.slice(4).trim()
              } else if (line.startsWith('data: ')) {
                try {
                  onData(JSON.parse(line.slice(6)) as FullVehicleStatus2)
                } catch {
                  // ignore malformed SSE frames
                }
              }
            }
          }

          if (!aborted) {
            onReconnecting?.()
            controller = new AbortController()
            await interruptibleSleep(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)])
            attempt++
          }
        } catch (err) {
          if ((err as Error).name === 'AbortError') return
          if (aborted) return
          if (!acked) {
            if (initialAttempt >= MAX_INITIAL_ATTEMPTS) { onError(); return }
            initialAttempt++
            onReconnecting?.()
            controller = new AbortController()
            await interruptibleSleep(PRE_ACK_BACKOFF_MS[Math.min(initialAttempt - 1, PRE_ACK_BACKOFF_MS.length - 1)])
            if (aborted) return
            continue
          }
          postAckFailures++
          if (postAckFailures > MAX_POST_ACK_FAILURES) { onError(); return }
          onReconnecting?.()
          controller = new AbortController()
          await interruptibleSleep(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)])
          attempt++
        }
      }
    })()

    return () => {
      aborted = true
      wakeFromSleep?.()
      controller.abort()
    }
  }

  async sendCommand(event: string, payload: Record<string, unknown> = {}): Promise<void> {
    const endpoint = COMMAND_ENDPOINTS[event]
    if (!endpoint) throw new Error(`Unknown command: ${event}`)

    try {
      await apiClient.post(endpoint, Object.keys(payload).length > 0 ? payload : undefined)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        throw new Error(err.message)
      }
      throw err
    }
  }

  async forceRefresh(): Promise<void> {
    try {
      await apiClient.post('/api/v1/refresh')
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422) throw new Error('12V battery is too low to wake the car')
        if (err.status === 429) throw new Error('Daily refresh limit reached')
        if ([409, 502, 503, 504].includes(err.status)) {
          if (err.status === 409) throw new ApiError('A refresh is already in progress — please wait', 409)
          if (err.status === 502) throw new ApiError("Couldn't reach Hyundai — try again shortly", 502)
          if (err.status === 503) throw new ApiError('Hyundai service unavailable — try again shortly', 503)
          throw new ApiError("Car didn't respond in time — try again shortly", 504)
        }
      }
      throw err
    }
  }
}
